import React, { useContext, useEffect, useState } from "react";
import SystemModal from "../../Components/Common/SystemModal";
import Loader from "../../Components/Common/Loader";
import { toast } from "react-hot-toast";
import ajaxProduct from "../../util/remote/ajaxProduct";
import ajaxStores from "../../util/remote/ajaxStores";
import Select from "react-select";
import ClientContext from "../../Context/ClientContext";

const UpdateInventoryRecord = (props) => {
  const record = props.data;
  const { clientList } = useContext(ClientContext);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [transactionType, setTransactionType] = useState(null); // 'in' or 'out'

  // Form state
  const [form, setForm] = useState({
    id: record?.id || "",
    product_id: record?.product_id || "",
    store_id: record?.store_id || "",
    client_id: record?.client_id || "",
    quantity_in: "",
    quantity_out: "",
    unit_cost: "",
    description: record?.description || "",
    date_added: record?.date_added?.db || "",
  });

  // Helper to extract quantity (handles object or plain number)
  const getQuantityValue = (qtyObj) => {
    if (!qtyObj) return "";
    if (typeof qtyObj === "object") {
      return (
        qtyObj.quantity_in || qtyObj.quantity_out || qtyObj.quantity_in_c || ""
      );
    }
    return qtyObj;
  };

  // Load dropdown data (products and stores only – clients come from context)
  useEffect(() => {
    const loadData = async () => {
      const productRes = await ajaxProduct.fetchProductList();
      if (productRes.status === "OK") setProducts(productRes.details);
      const storeRes = await ajaxStores.fetchStores();
      if (storeRes.status === "OK") setStores(storeRes.details);
    };
    loadData();
  }, []);

  // Populate form when record changes and detect transaction type
  useEffect(() => {
    if (record) {
      const qtyIn = parseFloat(getQuantityValue(record.quantity_in));
      const qtyOut = parseFloat(getQuantityValue(record.quantity_out));
      const isInTransaction = qtyIn > 0;
      const isOutTransaction = qtyOut > 0;

      // Determine transaction type (prefer 'in' if both appear, but normally only one is non-zero)
      let type = null;
      if (isInTransaction && !isOutTransaction) type = "in";
      else if (isOutTransaction && !isInTransaction) type = "out";
      else if (isInTransaction && isOutTransaction) type = "in"; // fallback

      setTransactionType(type);

      setForm({
        id: record.id,
        product_id: record.product_id,
        store_id: record.store_id || "",
        client_id: record.client_id || "",
        quantity_in: qtyIn > 0 ? qtyIn : "",
        quantity_out: qtyOut > 0 ? qtyOut : "",
        unit_cost: record.unit_cost || "",
        description: record.description || "",
        date_added: record.date_added?.db || "",
      });
    }
  }, [record]);

  const handleSubmit = async (controls) => {
    if (!form.product_id || !form.date_added) {
      toast.error("Product and Date are required.");
      return;
    }
    setLoading(true);
    const payload = {
      id: form.id,
      product_id: form.product_id,
      store_id: form.store_id || null,
      client_id: form.client_id || null,
      quantity_in: parseFloat(form.quantity_in) || 0,
      quantity_out: parseFloat(form.quantity_out) || 0,
      unit_cost: parseFloat(form.unit_cost) || 0,
      description: form.description,
      date_added: form.date_added,
    };
    const res = await ajaxProduct.updateInventoryRecord(payload);
    setLoading(false);
    if (res.status === "OK") {
      toast.success("Record updated successfully");
      controls.close();
      if (props.function) props.function(); // refresh list
    } else {
      toast.error(res.message || "Update failed");
    }
  };

  const RenderFooter = (controls) => {
    if (loading) return <Loader />;
    return (
      <>
        <button
          className="btn ripple btn-dark"
          type="button"
          onClick={controls.close}
        >
          Close
        </button>
        <button
          type="button"
          className="btn ripple btn-success"
          onClick={() => handleSubmit(controls)}
        >
          Save changes
        </button>
      </>
    );
  };

  return (
    <SystemModal
      title="Edit Inventory Record"
      id="modal-update-inventory-record"
      size="lg"
      footer={RenderFooter}
    >
      <div className="row">
        <div className="mb-3 col-md-6">
          <label>Product *</label>
          <Select
            options={products}
            getOptionLabel={(opt) => opt.product_name}
            getOptionValue={(opt) => opt.id}
            value={products.find((p) => p.id === form.product_id)}
            onChange={(opt) => setForm({ ...form, product_id: opt?.id || "" })}
            placeholder="Select product"
          />
        </div>
        <div className="mb-3 col-md-6">
          <label>Store</label>
          <Select
            options={stores}
            getOptionLabel={(opt) => opt.storeName}
            getOptionValue={(opt) => opt.id}
            value={stores.find((s) => s.id === form.store_id)}
            onChange={(opt) => setForm({ ...form, store_id: opt?.id || "" })}
            isClearable
          />
        </div>
        <div className="mb-3 col-md-6">
          <label>Client</label>
          <Select
            options={Array.isArray(clientList) ? clientList : []}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.client_id}
            value={
              form.client_id
                ? Array.isArray(clientList) &&
                  clientList.find((c) => c.client_id === form.client_id)
                : null
            }
            onChange={(opt) =>
              setForm({ ...form, client_id: opt?.client_id || "" })
            }
            isClearable
            placeholder="Select client (optional)"
          />
        </div>
        <div className="mb-3 col-md-6">
          <label>Date *</label>
          <input
            type="date"
            className="form-control"
            value={form.date_added}
            onChange={(e) => setForm({ ...form, date_added: e.target.value })}
            required
          />
        </div>

        {/* Quantity In field – disabled if transaction type is 'out' */}
        <div className="mb-3 col-md-6">
          <label>Quantity In</label>
          <input
            type="number"
            step="any"
            className="form-control"
            value={form.quantity_in}
            onChange={(e) => setForm({ ...form, quantity_in: e.target.value })}
            disabled={transactionType === "out"}
            placeholder={
              transactionType === "out" ? "Not applicable (outgoing)" : ""
            }
          />
        </div>

        {/* Quantity Out field – disabled if transaction type is 'in' */}
        <div className="mb-3 col-md-6">
          <label>Quantity Out</label>
          <input
            type="number"
            step="any"
            className="form-control"
            value={form.quantity_out}
            onChange={(e) => setForm({ ...form, quantity_out: e.target.value })}
            disabled={transactionType === "in"}
            placeholder={
              transactionType === "in" ? "Not applicable (incoming)" : ""
            }
          />
        </div>

        <div className="mb-3 col-md-6">
          <label>Unit Cost</label>
          <input
            type="number"
            step="any"
            className="form-control"
            value={form.unit_cost}
            onChange={(e) => setForm({ ...form, unit_cost: e.target.value })}
          />
        </div>
        <div className="mb-3 col-md-12">
          <label>Description</label>
          <textarea
            className="form-control"
            rows="3"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
      </div>
    </SystemModal>
  );
};

export default UpdateInventoryRecord;
